// What the outputs say about the information the work produces, read the infonomics way: the information assets the
// outputs are states of, and each output with a value (set by a user, or derived by LINQ from how it is used), the signals its derived value rests on, where the
// value lands (the roles and systems credited with it), and the friction behind the outputs nothing uses. Built from
// the analysis graph of what is looked at (every action, output and outcome with its figures) and the totals of its
// models
import { AnalysisEdge, AnalysisNode, isUseOf } from '../common/analysis-model';
import { DERIVED_VALUE_WEIGHTS, derivedValueOutsideSystems, heldOutsideSystems, InformationHeld, informationHeld } from '../common/information-value';
import { EDGE_TYPE, NODE_SUPERTYPE, NODE_TYPE, OUTPUT_VALUE_SOURCE, SYSTEM_KIND, WORK_TYPE } from '../common/linqset-base';
import { ASSET_CLASSIFICATION, ASSET_CLASSIFICATION_LABELS, SENSITIVE_CLASSIFICATIONS } from '../common/organisation';
import type { InformationAsset, NodeGroupModelInsights } from '../common/organisation';
import { AssetLinkSuggestion, LinkableInformation, suggestAssetLinks } from './asset-links';

export type InformationState = 'Revenue bearing' | 'Valued' | 'In use' | 'Dead end';

// an action (or outcome) an output passes through, in its model
export interface InformationStep {
    key: string;
    name: string;
    modelKey: string;
    model: string;
    outcome: boolean;
}

// an outcome the outputs realise, with the value and revenue set on it and what it takes to realise
export type OutcomeState = 'Revenue bearing' | 'Valued' | 'Not valued';
export interface InformationOutcome {
    key: string;
    name: string;
    modelKey: string;
    model: string;
    items: number;                  // a year
    userValue: number;              // per item, set on the outcome; 0 when none
    userRevenue: number;            // per item, set on the outcome; 0 when none
    value: number;                  // a year
    revenue: number;                // a year
    realisedBy: InformationStep[];  // the outputs realising it, in the graph shown
    hours: number;                  // cumulative hours a year to realise it
    cost: number;                   // cumulative cost a year to realise it
    state: OutcomeState;
}

export interface InformationOutput {
    key: string;
    name: string;
    modelKey: string;
    model: string;
    items: number;                  // a year
    value: number;                  // a year, from the values users set (own and flowing back from downstream)
    revenue: number;                // a year
    userValue: number;              // per item, set on the output; 0 when none
    userRevenue: number;            // per item, set on the output; 0 when none
    joinedOut: boolean;             // joined into another model: its value comes from there, so none is set on it
    valueSource: OUTPUT_VALUE_SOURCE | null;   // where a set value came from when it wasn't typed
    derivedValue: number;           // per item, 0 to 100 across the organisation
    type?: NODE_TYPE;               // the type how it is held is read from, to correct
    typeRead?: NODE_TYPE;           // LINQ's read of the type, where it's typed as plain information, to confirm
    held: InformationHeld;          // how it is held, from its type: beside the derived value, not part of it
    revenuePerItem: number;
    consumers: number;              // actions and outcomes using it directly
    consumerModels: number;         // other models using it
    outcomesReached: number;
    externalConsumers: number;
    hours: number;                  // cumulative hours a year to produce it
    cost: number;                   // cumulative cost a year to produce it
    state: InformationState;
    inUse: boolean;
    producedBy: InformationStep[];  // the actions that create it
    usedBy: InformationStep[];      // the actions and outcomes it goes to, in the graph shown
    assetKey?: string;              // the information asset it is a state of, if any
}

// one state of an information asset in the graph shown: an output, or information passed from one step to another
export interface AssetState {
    key: string;
    name: string;
    modelKey: string;
    model: string;
    output: boolean;
    held: InformationHeld;
    systems: string[];              // the systems attached to it
}

// what puts an asset at risk, read from its register details and how its states are held
export type AssetRiskKind = 'noOwner' | 'outsideRecord' | 'manyRecords' | 'sensitiveOutside';
export interface AssetRisk {
    kind: AssetRiskKind;
    text: string;
}

// a derived value this high marks an asset as valuable, as information risk reads outputs
const VALUABLE_DERIVED_VALUE = 50;

// an information asset: the information itself, which its states (outputs, and information passed between steps) are
// stages of. Its figures are its states' in what is looked at, and how it is used is read across the whole workspace
export interface InformationAssetSummary {
    key: string;
    name: string;
    description: string;
    states: AssetState[];           // in the graph shown, outputs first
    numberOfStates: number;         // in the models looked at, including any not in the graph shown
    models: number;                 // models looked at that it has states in
    items: number;                  // a year: the most of any state, as the same items pass through each
    value: number;                  // a year: the most of any state, as value flows back along the chain
    revenue: number;                // a year: the most of any of its outputs
    hours: number;                  // a year, on all its states
    cost: number;                   // a year, on all its states
    derivedValue?: number;          // 0 to 100 across the assets of the organisation; not known until insights are worked out again
    consumers: number;              // actions and outcomes using it, not counting the work moving it between its states
    consumerModels: number;         // models other than its own using it
    outcomesReached: number;
    externalConsumers: number;
    valuePerItem: number;
    revenuePerItem: number;
    heldOutside: number;            // states held in local tools, files, on paper or by word of mouth
    unsaid: number;                 // states whose holding isn't said
    state: InformationState;
    // the register
    owner?: string;                 // the owning role's name
    classification?: ASSET_CLASSIFICATION | null;
    systemOfRecord?: string;        // the name of the system holding its authoritative copy
    risks: AssetRisk[];
}

// an information asset of the organisation, with its insights in what is looked at
export interface AssetInput {
    key: string;
    name: string;
    description?: string;
    insights?: NodeGroupModelInsights;
    ownerKey?: string | null;
    owner?: string;                 // its name, when the role is in the organisation
    classification?: ASSET_CLASSIFICATION | null;
    systemOfRecordKey?: string | null;
    systemOfRecord?: string;        // its name, when the system is in the organisation
}

export interface InformationTotals {
    outputs: number;
    items: number;
    value: number;                  // value produced a year, from the models' totals (counted once across models)
    revenue: number;
    withValue: number;              // outputs with a set value or revenue
    setByHand: number;              // outputs whose value a user typed
    accepted: number;               // outputs whose value was accepted from LINQ's derived value or AI's suggestion
    inUseOnly: number;              // outputs in use with no value set: valued by use alone
    derived: number;                // outputs with a derived value
    deadEnds: number;
    frictionCost: number;           // a year: dead ends, exception work, work not adding value, re-keying and manual bridges, as the EPM scorecard counts it
    frictionHours: number;
    deadEndCost: number;            // a year, behind the dead ends
    deadEndHours: number;
    exceptionCost: number;          // a year, on exception work: actions on a detour from the main flow, or marked as such
    exceptionHours: number;
    exceptionActions: number;
    wasteCost: number;              // a year, on work marked as not adding value
    wasteHours: number;
    wasteActions: number;
    detours: number;                // detours from the main flow, each where items leave and return
    rekeyCost: number;              // a year, typing paper or spoken information into a system
    rekeyHours: number;
    rekeySteps: number;
    bridgeCost: number;             // a year, carrying information between systems by hand, beyond what is counted above
    bridgeHours: number;
    bridges: number;
    inUseCost: number;              // a year, behind the outputs in use with no value set: kept out of friction by their use
    inUseHours: number;
    heldOutside: { share: number; outputs: number; unsaid: number };   // the derived value held in files, on paper or by word of mouth
    assets: number;                 // information assets with a state in what is looked at
    linkedOutputs: number;          // outputs that are a state of an asset
    assetsHeldOutside: number;      // assets with a state held outside a system with controls around it
    assetsAtRisk: number;           // assets with anything in their register or holding that puts them at risk
}

// one of the signals a derived value rests on, with the output that has the most of it
export interface SignalSummary {
    key: keyof typeof DERIVED_VALUE_WEIGHTS;
    label: string;
    description: string;
    weight: number;
    leader?: { name: string; model: string; amount: number };
    unit: 'per item' | 'count';
}

// a role or system, with its share of the value and revenue the work it does creates: its value contribution, so the
// roles' add up, and the systems', to the value of the work they do (see value-contribution.ts). Beside it, the value it
// supports: every output it works towards, at its full value
export interface ValueHolder {
    key: string;
    name: string;
    kind: 'Role' | 'System';
    value: number;                  // value contribution a year
    revenue: number;                // revenue contribution a year
    supported: number;              // value supported a year
    outputsSupported: number;
}

// how the outputs, and the information passed between steps, are held: by kind, with how many are LINQ's read
export interface HeldSummary {
    held: InformationHeld;
    outputs: number;
    outputsRead: number;            // of those, typed as plain information, read by LINQ from their systems and names
    derivedShare: number;           // of the outputs' derived value, per item times the items a year
    handoffs: number;               // pieces of information passed between steps
    handoffsRead: number;
}

export interface InformationAnalysis {
    assets: InformationAssetSummary[];  // highest derived value first
    linkSuggestions: AssetLinkSuggestion[];  // information that looks like states of one asset, from its name, not yet linked
    outputs: InformationOutput[];   // highest derived value first
    outcomes: InformationOutcome[]; // most revenue first, then most value
    totals: InformationTotals;
    signals: SignalSummary[];
    holders: ValueHolder[];         // most value contribution first
    held: HeldSummary[];            // in a system of record first, not said last; only the kinds there are
}

export interface InformationAnalysisInput {
    nodes: AnalysisNode[];
    edges: AnalysisEdge[];
    modelNames: { [modelKey: string]: string };
    totals: { value: number; revenue: number };
    components: ValueHolder[];
    assets?: AssetInput[];          // the organisation's information assets
    systemKinds?: { [systemKey: string]: SYSTEM_KIND };   // system of record, business system or local tool, by system
    // the friction read from how information moves, as the EPM scorecard counts it: re-keying, and the manual bridges'
    // time beyond the exception work and re-keying. Left out when the scorecard can't be built
    informationFriction?: { rekeyCost: number; rekeyHours: number; rekeySteps: number; bridgeCost: number; bridgeHours: number; bridges: number };
}

// the ways information is held, the most controlled first
export const HELD_ORDER: InformationHeld[] = ['record', 'business', 'system', 'local', 'file', 'paper', 'verbal', 'unsaid'];

export const SIGNALS: Omit<SignalSummary, 'leader' | 'weight'>[] = [
    { key: 'revenue', label: 'Revenue reached', description: 'Revenue per item that flows back to the output from the outputs and outcomes it leads to', unit: 'per item' },
    { key: 'value', label: 'Value reached', description: 'Value per item set by users that flows back to the output', unit: 'per item' },
    { key: 'outcomes', label: 'Outcomes reached', description: 'Outcomes downstream of the output, however far', unit: 'count' },
    { key: 'reuse', label: 'Reuse', description: 'Actions and outcomes that use the output directly, in its model and others', unit: 'count' },
    { key: 'models', label: 'Other models', description: 'Other models the output is used in', unit: 'count' },
    { key: 'external', label: 'External organisations', description: 'External organisations performing the actions that use the output', unit: 'count' },
];

const stepOf = (n: AnalysisNode, modelNames: { [key: string]: string }): InformationStep =>
    ({ key: n._key, name: n.name, modelKey: n.modelKey, model: modelNames[n.modelKey] || '', outcome: n.superType === NODE_SUPERTYPE.OUTCOMES });

// the organisation's information assets, with their figures in the insights of what is looked at, and the names of the
// roles and systems in their register, from the organisation model's nodes
export const assetInputs = (assets: InformationAsset[] | undefined, insights?: { informationAssets?: NodeGroupModelInsights[] },
    organisationNodes: { _key: string; name: string }[] = []): AssetInput[] => {
    const nameOf = (key?: string | null) => (key ? organisationNodes.find(n => n._key === key)?.name : undefined);
    return (assets || []).map(a => ({
        key: a._key, name: a.name, description: a.description, insights: insights?.informationAssets?.find(i => i.groupKey === a._key),
        ownerKey: a.ownerKey || null, owner: nameOf(a.ownerKey), classification: a.classification || null,
        systemOfRecordKey: a.systemOfRecordKey || null, systemOfRecord: nameOf(a.systemOfRecordKey),
    }));
};

// what puts an asset at risk: valuable with no owner; states held outside its system of record, or in several systems
// of record with none set; sensitive information with states kept outside a controlled system
const assetRisks = (a: Omit<InformationAssetSummary, 'risks'>, input: AssetInput, systemKinds: { [key: string]: SYSTEM_KIND } = {}): AssetRisk[] => {
    const risks: AssetRisk[] = [];
    const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
    if (!input.ownerKey && (a.value > 0 || a.revenue > 0 || (a.derivedValue || 0) >= VALUABLE_DERIVED_VALUE))
        risks.push({ kind: 'noOwner', text: 'Valuable, but no role owns it' });
    const said = a.states.filter(s => s.held !== 'unsaid');
    if (input.systemOfRecordKey) {
        const outside = said.filter(s => !s.systems.includes(input.systemOfRecordKey as string)).length;
        if (outside) risks.push({ kind: 'outsideRecord', text: `${plural(outside, 'state is', 'states are')} held outside its system of record${input.systemOfRecord ? ', ' + input.systemOfRecord : ''}` });
    } else {
        const records = new Set(a.states.flatMap(s => s.systems).filter(k => systemKinds[k] === SYSTEM_KIND.RECORD)).size;
        if (records > 1) risks.push({ kind: 'manyRecords', text: `Held in ${records} systems of record, with none set as its own` });
    }
    if (input.classification && SENSITIVE_CLASSIFICATIONS.includes(input.classification) && a.heldOutside)
        risks.push({ kind: 'sensitiveOutside', text: `${ASSET_CLASSIFICATION_LABELS[input.classification]} information, with ${plural(a.heldOutside, 'state', 'states')} kept outside a controlled system` });
    return risks;
};

export const buildInformationAnalysis = (input: InformationAnalysisInput): InformationAnalysis => {
    const { edges, modelNames } = input;
    const byKey = new Map(input.nodes.map(n => [n._key, n]));
    const outputsNodes = input.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION);

    // the edges carrying each output, by its key
    const carrying = new Map<string, AnalysisEdge[]>();
    edges.forEach(e => {
        const key = e.informationKey || (byKey.get(e.to)?.superType === NODE_SUPERTYPE.INFORMATION ? e.to : '');
        if (!key) return;
        const list = carrying.get(key);
        if (list) list.push(e);
        else carrying.set(key, [e]);
    });

    const outputs: InformationOutput[] = outputsNodes.map(o => {
        const carried = carrying.get(o._key) || [];
        const producers = new Map<string, AnalysisNode>();
        const users = new Map<string, AnalysisNode>();
        carried.forEach(e => {
            const from = byKey.get(e.from);
            const to = byKey.get(e.to);
            if (from && from.superType === NODE_SUPERTYPE.ACTION && e.type !== EDGE_TYPE.USED_IN) producers.set(from._key, from);
            if (to && to._key !== o._key && isUseOf(e, o) && (to.superType === NODE_SUPERTYPE.ACTION || to.superType === NODE_SUPERTYPE.OUTCOMES)) users.set(to._key, to);
        });
        const inUse = (o.consumers || 0) > 0 || users.size > 0;
        const state: InformationState = o.revenue > 0 ? 'Revenue bearing' : o.value > 0 ? 'Valued' : inUse ? 'In use' : 'Dead end';
        return {
            key: o._key,
            name: o.name,
            modelKey: o.modelKey,
            model: modelNames[o.modelKey] || '',
            items: o.items || 0,
            value: o.value || 0,
            revenue: o.revenue || 0,
            userValue: o.userValue || 0,
            userRevenue: o.userRevenue || 0,
            joinedOut: !!o.joinedOut,
            valueSource: o.valueSource || null,
            derivedValue: o.derivedValue || 0,
            type: o.type,
            typeRead: o.heldAsRead ? o.heldAs : undefined,
            held: informationHeld(o.heldAs ?? o.type, (o.heldBy || []).map(k => input.systemKinds?.[k]).filter((k): k is SYSTEM_KIND => !!k)),
            revenuePerItem: o.items > 0 ? (o.revenue || 0) / o.items : 0,
            consumers: Math.max(o.consumers || 0, users.size),
            consumerModels: o.consumerModels || 0,
            outcomesReached: o.outcomesReached || 0,
            externalConsumers: o.externalConsumers || 0,
            hours: o.totalHours || 0,
            cost: o.totalCost || 0,
            state,
            inUse,
            producedBy: [...producers.values()].map(n => stepOf(n, modelNames)),
            usedBy: [...users.values()].map(n => stepOf(n, modelNames)),
            assetKey: o.assetKey,
        };
    }).sort((a, b) => b.derivedValue - a.derivedValue || b.value - a.value || b.revenue - a.revenue || a.name.localeCompare(b.name));

    // the outcomes, with the outputs that realise them
    const outcomes: InformationOutcome[] = input.nodes.filter(n => n.superType === NODE_SUPERTYPE.OUTCOMES).map(o => {
        const realisedBy = new Map<string, AnalysisNode>();
        edges.forEach(e => {
            const from = e.to === o._key ? byKey.get(e.from) : undefined;
            if (from?.superType === NODE_SUPERTYPE.INFORMATION) realisedBy.set(from._key, from);
        });
        const value = o.value || 0, revenue = o.revenue || 0;
        return {
            key: o._key,
            name: o.name,
            modelKey: o.modelKey,
            model: modelNames[o.modelKey] || '',
            items: o.items || 0,
            userValue: o.userValue || 0,
            userRevenue: o.userRevenue || 0,
            value,
            revenue,
            realisedBy: [...realisedBy.values()].map(n => stepOf(n, modelNames)),
            hours: o.totalHours || 0,
            cost: o.totalCost || 0,
            state: revenue > 0 ? 'Revenue bearing' : value > 0 ? 'Valued' : 'Not valued',
        } as InformationOutcome;
    }).sort((a, b) => b.revenue - a.revenue || b.value - a.value || a.name.localeCompare(b.name));

    const deadEnds = outputs.filter(o => o.state === 'Dead end');
    const deadEndCost = deadEnds.reduce((t, o) => t + o.cost, 0);
    const deadEndHours = deadEnds.reduce((t, o) => t + o.hours, 0);
    // exception work is the other friction: the actions on a detour from the main flow, or marked as exception work
    const exceptionActions = input.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && n.exceptionWork);
    const exceptionCost = exceptionActions.reduce((t, a) => t + (a.cost || 0), 0);
    const exceptionHours = exceptionActions.reduce((t, a) => t + (a.hours || 0), 0);
    // and the other waste: actions marked as not adding value
    const wasteActions = input.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && !n.exceptionWork && n.workKind === WORK_TYPE.NOT_VALUE_ADDING);
    const wasteCost = wasteActions.reduce((t, a) => t + (a.cost || 0), 0);
    const wasteHours = wasteActions.reduce((t, a) => t + (a.hours || 0), 0);
    const inUseOnly = outputs.filter(o => o.inUse && !o.userValue && !o.revenue && !o.joinedOut);
    const moved = input.informationFriction || { rekeyCost: 0, rekeyHours: 0, rekeySteps: 0, bridgeCost: 0, bridgeHours: 0, bridges: 0 };
    const totals: InformationTotals = {
        outputs: outputs.length,
        items: outputs.reduce((t, o) => t + o.items, 0),
        value: input.totals.value,
        revenue: input.totals.revenue,
        withValue: outputs.filter(o => o.value > 0 || o.revenue > 0).length,
        setByHand: outputs.filter(o => o.userValue > 0 && !o.valueSource).length,
        accepted: outputs.filter(o => o.userValue > 0 && !!o.valueSource).length,
        inUseOnly: inUseOnly.length,
        derived: outputs.filter(o => o.derivedValue > 0).length,
        deadEnds: deadEnds.length,
        frictionCost: deadEndCost + exceptionCost + wasteCost + moved.rekeyCost + moved.bridgeCost,
        frictionHours: deadEndHours + exceptionHours + wasteHours + moved.rekeyHours + moved.bridgeHours,
        wasteCost,
        wasteHours,
        wasteActions: wasteActions.length,
        deadEndCost,
        deadEndHours,
        exceptionCost,
        exceptionHours,
        exceptionActions: exceptionActions.length,
        ...moved,
        detours: new Set(exceptionActions.map(a => a.onDetour && a.detourSplitKey && a.detourJoinKey ? `${a.detourSplitKey}>${a.detourJoinKey}` : a._key)).size,
        inUseCost: inUseOnly.reduce((t, o) => t + o.cost, 0),
        inUseHours: inUseOnly.reduce((t, o) => t + o.hours, 0),
        heldOutside: derivedValueOutsideSystems(outputs),
        assets: 0,
        linkedOutputs: 0,
        assetsHeldOutside: 0,
        assetsAtRisk: 0,
    };

    const amountOf = (o: InformationOutput, key: SignalSummary['key']) => {
        switch (key) {
            case 'revenue': return o.revenuePerItem;
            case 'value': return o.items > 0 ? o.value / o.items : 0;
            case 'outcomes': return o.outcomesReached;
            case 'reuse': return o.consumers;
            case 'models': return o.consumerModels;
            case 'external': return o.externalConsumers;
        }
    };
    const signals: SignalSummary[] = SIGNALS.map(s => {
        const leader = outputs.map(o => ({ o, amount: amountOf(o, s.key) })).sort((a, b) => b.amount - a.amount)[0];
        return { ...s, weight: DERIVED_VALUE_WEIGHTS[s.key], leader: leader && leader.amount > 0 ? { name: leader.o.name, model: leader.o.model, amount: leader.amount } : undefined };
    });

    // ranked by the revenue their work creates where there is any, as money compares across the organisation; else by value
    const byRevenue = input.components.some(c => c.revenue > 0);
    const holders = input.components.filter(c => c.value > 0 || c.revenue > 0)
        .sort((a, b) => (byRevenue ? b.revenue - a.revenue || b.value - a.value : b.value - a.value) || b.outputsSupported - a.outputsSupported);

    // how the information is held: the outputs, and each piece of information passed from one step to another
    const kindsOf = (systems: string[] | undefined) => (systems || []).map(k => input.systemKinds?.[k]).filter((k): k is SYSTEM_KIND => !!k);
    const actionKeys = new Set(input.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => n._key));
    const handoffs = new Map<string, { held: InformationHeld; read: boolean }>();
    input.edges.forEach(e => {
        if (e.informationKey && actionKeys.has(e.from) && actionKeys.has(e.to) && !handoffs.has(e.informationKey))
            handoffs.set(e.informationKey, { held: informationHeld(e.informationType, kindsOf(e.informationSystems)), read: !!e.informationTypeRead });
    });
    const weightOf = (o: InformationOutput) => (o.derivedValue || 0) * Math.max(o.items || 0, 1);
    const totalWeight = outputs.reduce((t, o) => t + weightOf(o), 0);
    const held: HeldSummary[] = HELD_ORDER.map(h => {
        const these = outputs.filter(o => o.held === h);
        const passed = [...handoffs.values()].filter(x => x.held === h);
        return {
            held: h, outputs: these.length, outputsRead: these.filter(o => o.typeRead !== undefined).length,
            derivedShare: totalWeight > 0 ? these.reduce((t, o) => t + weightOf(o), 0) / totalWeight : 0,
            handoffs: passed.length, handoffsRead: passed.filter(x => x.read).length,
        };
    }).filter(x => x.outputs || x.handoffs);

    // the information assets, from their states in the graph shown (each output, and each piece of information passed
    // between steps, that is a state of one) and their insights in the models looked at
    const statesOf = new Map<string, AssetState[]>();
    const addState = (assetKey: string | undefined, state: AssetState) => {
        if (!assetKey) return;
        const list = statesOf.get(assetKey);
        if (!list) statesOf.set(assetKey, [state]);
        else if (!list.some(x => x.key === state.key)) list.push(state);
    };
    outputs.forEach(o => addState(o.assetKey, { key: o.key, name: o.name, modelKey: o.modelKey, model: o.model, output: true, held: o.held, systems: byKey.get(o.key)?.heldBy || [] }));
    input.edges.forEach(e => {
        if (e.informationAssetKey && e.informationKey && actionKeys.has(e.from) && actionKeys.has(e.to) && !byKey.has(e.informationKey))
            addState(e.informationAssetKey, { key: e.informationKey, name: e.informationName, modelKey: e.modelKey, model: modelNames[e.modelKey] || '', output: false,
                held: informationHeld(e.informationType, kindsOf(e.informationSystems)), systems: e.informationSystems || [] });
    });
    const assets: InformationAssetSummary[] = (input.assets || []).flatMap(a => {
        const states = (statesOf.get(a.key) || []).sort((x, y) => Number(y.output) - Number(x.output) || x.name.localeCompare(y.name));
        const i = a.insights;
        if (!states.length && !(i?.numberOfStates)) return [];
        const v = i?.assetValue;
        const own = outputs.filter(o => o.assetKey === a.key);
        const revenue = Math.max(0, ...own.map(o => o.revenue));
        const value = Math.max(i?.value || 0, ...own.map(o => o.value));
        // until the insights are worked out again, how it's used is read from its outputs
        const inUse = v ? v.consumers > 0 : own.some(o => o.inUse);
        const summary: Omit<InformationAssetSummary, 'risks'> = {
            key: a.key,
            name: a.name,
            description: a.description || '',
            states,
            numberOfStates: Math.max(i?.numberOfStates || 0, states.length),
            models: new Set(states.map(x => x.modelKey)).size,
            items: Math.max(i?.numberProcessed || 0, ...own.map(o => o.items)),
            value,
            revenue,
            hours: i?.hours || 0,
            cost: i?.allocatedCostsByType?.total || 0,
            derivedValue: v?.derivedValue,
            consumers: v?.consumers || 0,
            consumerModels: v?.consumerModels || 0,
            outcomesReached: v?.outcomesReached || 0,
            externalConsumers: v?.externalConsumers || 0,
            valuePerItem: v?.valuePerItem || 0,
            revenuePerItem: v?.revenuePerItem || 0,
            heldOutside: states.filter(x => heldOutsideSystems(x.held)).length,
            unsaid: states.filter(x => x.held === 'unsaid').length,
            state: revenue > 0 ? 'Revenue bearing' : value > 0 ? 'Valued' : inUse ? 'In use' : 'Dead end',
            owner: a.owner,
            classification: a.classification,
            systemOfRecord: a.systemOfRecord,
        };
        return [{ ...summary, risks: assetRisks(summary, a, input.systemKinds) }];
    }).sort((a, b) => (b.derivedValue ?? -1) - (a.derivedValue ?? -1) || b.value - a.value || b.revenue - a.revenue || a.name.localeCompare(b.name));
    const assetKeys = new Set(assets.map(a => a.key));
    totals.assets = assets.length;
    totals.linkedOutputs = outputs.filter(o => o.assetKey && assetKeys.has(o.assetKey)).length;
    totals.assetsHeldOutside = assets.filter(a => a.heldOutside > 0).length;
    totals.assetsAtRisk = assets.filter(a => a.risks.length > 0).length;

    // the information that could be linked to an asset: the outputs, and the information passed between steps
    const linkable: LinkableInformation[] = [
        ...outputs.map(o => ({ key: o.key, name: o.name, modelKey: o.modelKey, model: o.model, assetKey: o.assetKey })),
        ...input.edges.filter(e => e.informationKey && actionKeys.has(e.from) && actionKeys.has(e.to) && !byKey.has(e.informationKey))
            .map(e => ({ key: e.informationKey, name: e.informationName, modelKey: e.modelKey, model: modelNames[e.modelKey] || '', assetKey: e.informationAssetKey })),
    ];
    const linkSuggestions = suggestAssetLinks(linkable, (input.assets || []).map(a => ({ key: a.key, name: a.name })));

    return { assets, linkSuggestions, outputs, outcomes, totals, signals, holders, held };
};
